(() => {
  const namespace = window.AerreRewards;

  if (!namespace || !namespace.config || namespace.controller) {
    return;
  }

  class AerreRewardsController {
    constructor(config) {
      this.config = config;
      this.gift = this.normalizeGiftConfig(config.gift);
      this.cart = this.normalizeCart(config.initialCart);
      this.adapter = this.resolveAdapter();
      this.confirmed = this.inspectCart(this.cart).coherent;
      this.desired = this.desiredFromCart(this.cart);
      this.desiredGiftFamily = this.preferredGiftFamily(this.cart);
      this.desiredAtomiserVariantId = this.preferredAtomiserVariantId(this.cart);
      this.desiredGiftVariantId = this.confirmedGiftVariantId(this.cart) || this.preferredGiftVariantId(this.cart);
      this.requested = null;
      this.processing = null;
      this.pending = false;
      this.checkoutInProgress = false;
      this.lastError = '';
      this.giftLastError = '';
      this.giftAnnouncement = '';
      this.lastSurface = null;
      this.giftFocusElement = null;
      this.giftFocusSelector = null;
      this.termsPopupPending = false;
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDrawerTermsClick = this.onDrawerTermsClick.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
    }

    start() {
      document.documentElement.dataset.aerreRewardsCohort = this.adapter.cohort;
      document.documentElement.dataset.aerreRewardsCountry = this.adapter.countryCode;
      document.documentElement.dataset.aerreRewardsCountryEligible = String(this.adapter.countryEligible);
      namespace.adapter = Object.freeze({ ...this.adapter });
      document.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onDrawerTermsClick, true);
      document.addEventListener('click', this.onClick);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.documentElement.addEventListener('cart:updated', this.onCartUpdated);
      this.updateUi();

      const treatmentEligible = this.adapter.countryEligible && this.adapter.cohort === 'treatment' && !this.config.entitled;
      const inspection = this.inspectCart(this.cart);

      if (!treatmentEligible && (this.hasExperimentState(this.cart) || this.hasManagedStaleState(this.cart))) {
        this.enqueue(false);
      } else if (this.operational && (this.desired || inspection.membershipItems.length > 0 || this.hasManagedStaleState(this.cart))) {
        this.enqueue(this.desired);
      } else if (this.hasManagedStaleState(this.cart)) {
        this.enqueue(false);
      }
    }

    get operational() {
      const inspection = this.inspectCart(this.cart);
      return this.adapter.countryEligible && this.adapter.cohort === 'treatment' && this.config.valid && !this.config.entitled && (this.config.available || inspection.membershipItems.length > 0);
    }

    normalizeCart(cart) {
      const normalized = cart && typeof cart === 'object' ? cart : {};
      normalized.items = Array.isArray(normalized.items) ? normalized.items : [];
      normalized.attributes = normalized.attributes && typeof normalized.attributes === 'object' ? normalized.attributes : {};
      return normalized;
    }

    normalizeGiftConfig(gift) {
      const normalized = gift && typeof gift === 'object' ? gift : {};
      normalized.enabled = Boolean(normalized.enabled);
      normalized.valid = Boolean(normalized.valid);
      normalized.property = String(normalized.property || '');
      normalized.version = String(normalized.version || '');
      normalized.legacyVersions = Array.isArray(normalized.legacyVersions)
        ? normalized.legacyVersions.map((version) => String(version || '')).filter(Boolean)
        : [];
      normalized.discountTitle = String(normalized.discountTitle || '').trim();
      normalized.familyPreferenceAttribute = String(normalized.familyPreferenceAttribute || 'aerre_rewards_gift_family');
      normalized.atomiserPreferenceAttribute = String(normalized.atomiserPreferenceAttribute || 'aerre_rewards_gift_atomiser_variant');
      normalized.variants = Array.isArray(normalized.variants)
        ? normalized.variants
          .map((variant, index) => ({
            id: Number(variant.id),
            productId: Number(variant.productId),
            title: String(variant.title || ''),
            family: variant.family === 'discovery' ? 'discovery' : 'atomiser',
            available: Boolean(variant.available),
            componentVariantIds: Array.isArray(variant.componentVariantIds)
              ? variant.componentVariantIds.map(Number).filter(Number.isFinite)
              : [],
            componentProductIds: Array.isArray(variant.componentProductIds)
              ? variant.componentProductIds.map(Number).filter(Number.isFinite)
              : [],
            order: index
          }))
          .filter((variant) => Number.isFinite(variant.id) && Number.isFinite(variant.productId))
        : [];
      normalized.defaultFamily = normalized.defaultFamily === 'atomiser' ? 'atomiser' : 'discovery';
      normalized.defaultAtomiserVariantId = Number(normalized.defaultAtomiserVariantId || normalized.defaultVariantId);

      if (!normalized.variants.some((variant) => variant.family === 'atomiser' && variant.id === normalized.defaultAtomiserVariantId)) {
        normalized.defaultAtomiserVariantId = normalized.variants.find((variant) => variant.family === 'atomiser' && variant.available)?.id
          || normalized.variants.find((variant) => variant.family === 'atomiser')?.id
          || null;
      }

      return normalized;
    }

    resolveAdapter() {
      // Elevate routes shoppers between whole themes. This variant must remain
      // self-contained, so legacy in-theme experiment globals cannot disable it.
      let cohort = this.config.defaultEnabled ? 'treatment' : 'control';
      let defaultOn = Boolean(this.config.defaultOn);

      if (this.config.previewAllowed) {
        const preview = new URLSearchParams(window.location.search).get('aerre_rewards_preview');

        if (preview === 'control') {
          cohort = 'control';
        } else if (preview === 'treatment') {
          cohort = 'treatment';
        } else if (preview === 'on') {
          cohort = 'treatment';
          defaultOn = true;
        } else if (preview === 'off') {
          cohort = 'treatment';
          defaultOn = false;
        }
      }

      const countryCode = String(this.config.countryCode || '').trim().toUpperCase();
      const experimentCountryCode = String(this.config.experimentCountryCode || '').trim().toUpperCase();
      const countryEligible = Boolean(experimentCountryCode) && countryCode === experimentCountryCode;

      if (!countryEligible) {
        cohort = 'control';
        defaultOn = false;
      }

      return { cohort, defaultOn, countryCode, experimentCountryCode, countryEligible };
    }

    sellingPlanId(item) {
      return item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? Number(item.selling_plan_allocation.selling_plan.id)
        : null;
    }

    isMembershipItem(item) {
      return Number(item.product_id) === Number(this.config.productId) || Number(item.variant_id) === Number(this.config.variantId);
    }

    isValidMembershipItem(item) {
      return Number(item.variant_id) === Number(this.config.variantId) && this.sellingPlanId(item) === Number(this.config.sellingPlanId);
    }

    giftVariant(variantId) {
      return this.gift.variants.find((variant) => variant.id === Number(variantId)) || null;
    }

    isManagedGiftItem(item) {
      const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
      const variant = this.giftVariant(item && item.variant_id);
      const marker = String(properties[this.gift.property] || '');

      return Boolean(variant)
        && Number(item && item.product_id) === variant.productId
        && (marker === this.gift.version || this.gift.legacyVersions.includes(marker));
    }

    isCurrentGiftItem(item) {
      const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
      return this.isManagedGiftItem(item) && String(properties[this.gift.property] || '') === this.gift.version;
    }

    normalizedDiscountTitle(value) {
      return String(value || '').trim().toLowerCase();
    }

    matchingGiftDiscountAmount(item) {
      const expectedTitle = this.normalizedDiscountTitle(this.gift.discountTitle);

      if (!expectedTitle) {
        return 0;
      }

      const lineAllocations = Array.isArray(item && item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      const lineAllocationAmount = lineAllocations.reduce((total, allocation) => {
        const application = allocation && allocation.discount_application && typeof allocation.discount_application === 'object'
          ? allocation.discount_application
          : {};

        if (this.normalizedDiscountTitle(application.title) !== expectedTitle) {
          return total;
        }

        return total + Math.max(0, Number(allocation.amount) || 0);
      }, 0);
      return lineAllocationAmount;
    }

    lineLevelDiscountAmount(item) {
      const reportedTotal = Number(item && item.line_level_total_discount);

      if (reportedTotal > 0) {
        return reportedTotal;
      }

      const originalLinePrice = Number(item && item.original_line_price);
      const finalLinePrice = Number(item && item.final_line_price);

      if (Number.isFinite(originalLinePrice) && Number.isFinite(finalLinePrice) && originalLinePrice >= finalLinePrice) {
        return originalLinePrice - finalLinePrice;
      }

      const lineAllocations = Array.isArray(item && item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];
      const lineAllocationAmount = lineAllocations.reduce(
        (total, allocation) => total + Math.max(0, Number(allocation && allocation.amount) || 0),
        0
      );

      return lineAllocationAmount;
    }

    isOrdinaryGiftCandidate(item) {
      const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
      const variant = this.giftVariant(item && item.variant_id);
      const marker = String(properties[this.gift.property] || '').trim();

      return Boolean(variant)
        && Number(item && item.product_id) === variant.productId
        && Number(item && item.quantity) > 0
        && marker === '';
    }

    hasCreditedOrdinaryGift(cart) {
      const normalized = this.normalizeCart(cart);

      if (!this.inspectCart(normalized).coherent) {
        return false;
      }

      return normalized.items.some((item) => {
        if (!this.isOrdinaryGiftCandidate(item)) {
          return false;
        }

        const originalUnitPrice = Number(item.original_price);
        return originalUnitPrice > 0
          && this.matchingGiftDiscountAmount(item) > 0
          && this.lineLevelDiscountAmount(item) >= originalUnitPrice;
      });
    }

    confirmedGiftVariantId(cart) {
      const inspection = this.inspectCart(cart);
      const giftItems = inspection.giftItems.filter((item) => this.isCurrentGiftItem(item));

      if (inspection.giftItems.length !== 1 || giftItems.length !== 1 || Number(giftItems[0].quantity) !== 1) {
        return null;
      }

      return Number(giftItems[0].variant_id);
    }

    preferredGiftFamily(cart) {
      const saved = this.attributeValue(cart, this.gift.familyPreferenceAttribute);

      if (saved === 'atomiser' || saved === 'discovery') {
        return saved;
      }

      const current = this.confirmedGiftVariantId(cart);
      return this.giftVariant(current)?.family || this.gift.defaultFamily;
    }

    preferredAtomiserVariantId(cart) {
      const saved = Number(this.attributeValue(cart, this.gift.atomiserPreferenceAttribute));
      const current = this.giftVariant(this.confirmedGiftVariantId(cart));

      if (this.giftVariant(saved)?.family === 'atomiser') {
        return saved;
      }

      if (current?.family === 'atomiser') {
        return current.id;
      }

      return this.gift.defaultAtomiserVariantId;
    }

    paidCartIds(cart) {
      const productIds = new Set();
      const variantIds = new Set();

      this.normalizeCart(cart).items.forEach((item) => {
        if (Number(item.quantity) <= 0 || this.isMembershipItem(item) || this.isManagedGiftItem(item)) {
          return;
        }

        productIds.add(Number(item.product_id));
        variantIds.add(Number(item.variant_id));
      });

      return { productIds, variantIds };
    }

    discoveryScore(variant, cart) {
      const bought = this.paidCartIds(cart);
      const componentCount = Math.max(variant.componentVariantIds.length, variant.componentProductIds.length);
      let unseen = 0;

      for (let index = 0; index < componentCount; index += 1) {
        const productId = Number(variant.componentProductIds[index]);
        const variantId = Number(variant.componentVariantIds[index]);

        if (!bought.productIds.has(productId) && !bought.variantIds.has(variantId)) {
          unseen += 1;
        }
      }

      return unseen;
    }

    rankedDiscoveries(cart) {
      return this.gift.variants
        .filter((variant) => variant.family === 'discovery' && variant.available)
        .sort((left, right) => this.discoveryScore(right, cart) - this.discoveryScore(left, cart) || left.order - right.order);
    }

    preferredGiftVariantId(cart) {
      const family = this.desiredGiftFamily || this.preferredGiftFamily(cart);

      if (family === 'discovery') {
        return this.rankedDiscoveries(cart)[0]?.id
          || this.gift.variants.find((variant) => variant.family === 'atomiser' && variant.available)?.id
          || null;
      }

      const atomiser = this.giftVariant(this.desiredAtomiserVariantId || this.preferredAtomiserVariantId(cart));

      if (atomiser?.family === 'atomiser' && atomiser.available) {
        return atomiser.id;
      }

      return this.gift.variants.find((variant) => variant.family === 'atomiser' && variant.available)?.id
        || this.rankedDiscoveries(cart)[0]?.id
        || null;
    }

    giftStateSafeForCheckout(cart, target) {
      const inspection = this.inspectCart(cart);

      if (inspection.giftItems.length === 0) {
        return true;
      }

      if (this.hasCreditedOrdinaryGift(cart)) {
        return false;
      }

      return Boolean(
        target
        && inspection.coherent
        && this.gift.enabled
        && this.gift.valid
        && inspection.giftItems.length === 1
        && Boolean(this.confirmedGiftVariantId(cart))
        && Number(inspection.giftItems[0].quantity) === 1
      );
    }

    membershipStateSafeForCheckout(cart) {
      const inspection = this.inspectCart(cart);
      return inspection.membershipItems.length === 0 || inspection.coherent;
    }

    hasManagedStaleState(cart) {
      const inspection = this.inspectCart(cart);

      if (!this.operational) {
        return inspection.membershipItems.length > 0 || inspection.giftItems.length > 0;
      }

      const membershipIsStale = inspection.membershipItems.length > 0 && !inspection.coherent;
      const giftIsStale = inspection.giftItems.length > 0 && !this.giftStateSafeForCheckout(cart, true);

      return membershipIsStale || giftIsStale;
    }

    inspectCart(cart) {
      const normalized = this.normalizeCart(cart);
      const membershipItems = normalized.items.filter((item) => this.isMembershipItem(item));
      const validItems = membershipItems.filter((item) => this.isValidMembershipItem(item));
      const giftItems = normalized.items.filter((item) => this.isManagedGiftItem(item));
      const hasMerchandise = normalized.items.some((item) => !this.isMembershipItem(item) && !this.isManagedGiftItem(item) && Number(item.quantity) > 0);
      const coherent = membershipItems.length === 1 && validItems.length === 1 && Number(validItems[0].quantity) === 1;

      return { membershipItems, validItems, giftItems, hasMerchandise, coherent };
    }

    attributeValue(cart, name) {
      const attributes = this.normalizeCart(cart).attributes;
      const value = attributes[name];
      return value === null || typeof value === 'undefined' ? '' : String(value);
    }

    hasExperimentState(cart) {
      const inspection = this.inspectCart(cart);
      const attributeNames = [
        this.config.preferenceAttribute,
        this.config.versionAttribute,
        this.config.assignmentAttribute,
        this.gift.familyPreferenceAttribute,
        this.gift.atomiserPreferenceAttribute,
        'membership_opt_in',
        'membership_opt_in_source'
      ];

      return inspection.membershipItems.length > 0 || inspection.giftItems.length > 0 || attributeNames.some((name) => this.attributeValue(cart, name) !== '');
    }

    desiredFromCart(cart) {
      const inspection = this.inspectCart(cart);

      if (!inspection.hasMerchandise || this.config.entitled || !this.adapter.countryEligible || this.adapter.cohort !== 'treatment') {
        return false;
      }

      const preference = this.attributeValue(cart, this.config.preferenceAttribute);

      if (preference === `off:${this.config.offerVersion}`) {
        return false;
      }

      if (preference === `on:${this.config.offerVersion}`) {
        return true;
      }

      if (inspection.coherent) {
        return true;
      }

      return this.adapter.defaultOn;
    }

    targetAttributes(target, hasMerchandise) {
      const attributes = {
        membership_opt_in: '',
        membership_opt_in_source: '',
        [this.config.assignmentAttribute]: ''
      };

      if (!hasMerchandise) {
        attributes[this.config.preferenceAttribute] = '';
        attributes[this.config.versionAttribute] = '';
        attributes[this.config.assignmentAttribute] = '';
        attributes[this.gift.familyPreferenceAttribute] = '';
        attributes[this.gift.atomiserPreferenceAttribute] = '';
        return attributes;
      }

      if (!this.adapter.countryEligible || this.config.entitled) {
        attributes[this.config.preferenceAttribute] = '';
        attributes[this.config.versionAttribute] = '';
        attributes[this.gift.familyPreferenceAttribute] = '';
        attributes[this.gift.atomiserPreferenceAttribute] = '';
        return attributes;
      }

      attributes[this.config.preferenceAttribute] = `${target ? 'on' : 'off'}:${this.config.offerVersion}`;
      attributes[this.config.versionAttribute] = this.config.offerVersion;
      attributes[this.gift.familyPreferenceAttribute] = this.desiredGiftFamily;
      attributes[this.gift.atomiserPreferenceAttribute] = String(this.desiredAtomiserVariantId || '');
      return attributes;
    }

    attributesMatch(cart, expected) {
      return Object.entries(expected).every(([name, value]) => this.attributeValue(cart, name) === value);
    }

    sectionsPayload() {
      const sections = Array.from(document.querySelectorAll('[data-aerre-rewards-section-id]'))
        .map((component) => component.dataset.aerreRewardsSectionId)
        .filter((sectionId, index, values) => sectionId && values.indexOf(sectionId) === index)
        .slice(0, 5);

      return {
        sections,
        sections_url: window.location.pathname + window.location.search
      };
    }

    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data) {
        const message = data && (data.description || data.message) ? data.description || data.message : response.statusText;
        throw new Error(message || 'Aerre Rewards cart request failed');
      }

      return data;
    }

    async updateCart(updates, attributes) {
      const body = { ...this.sectionsPayload() };

      if (updates && Object.keys(updates).length > 0) {
        body.updates = updates;
      }

      if (attributes) {
        body.attributes = attributes;
      }

      const cart = await this.post(`${this.config.cartUrl}/update.js`, body);

      if (!Array.isArray(cart.items)) {
        throw new Error('Aerre Rewards received an invalid cart response');
      }

      return this.normalizeCart(cart);
    }

    membershipAddItem() {
      return {
        id: this.config.variantId,
        quantity: 1,
        selling_plan: this.config.sellingPlanId,
        properties: {
          _aerre_rewards_offer: this.config.offerVersion
        }
      };
    }

    giftAddItem(variantId) {
      const variant = this.giftVariant(variantId);

      if (!variant) {
        throw new Error('Aerre Rewards gift variant is invalid');
      }

      return {
        id: variantId,
        quantity: 1,
        properties: {
          [this.gift.property]: this.gift.version,
          _aerre_rewards_gift_kind: variant.family
        }
      };
    }

    async addMembership(giftVariantId = null) {
      const items = [this.membershipAddItem()];

      if (giftVariantId !== null) {
        items.push(this.giftAddItem(giftVariantId));
      }

      return this.post(`${this.config.cartAddUrl}.js`, { items });
    }

    async addGift(variantId) {
      return this.post(`${this.config.cartAddUrl}.js`, {
        items: [this.giftAddItem(variantId)]
      });
    }

    async recoverCart() {
      const response = await fetch(`${this.config.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Aerre Rewards cart recovery failed');
      }

      const cart = await response.json();

      if (!Array.isArray(cart.items)) {
        throw new Error('Aerre Rewards received an invalid recovery response');
      }

      return this.normalizeCart(cart);
    }

    async refreshCartSections(cart) {
      const normalized = this.normalizeCart(cart);

      if (normalized.sections) {
        return normalized;
      }

      const attributeName = String(this.config.preferenceAttribute || '');

      if (attributeName) {
        return this.updateCart({}, {
          [attributeName]: this.attributeValue(normalized, attributeName)
        });
      }

      const firstItem = normalized.items.find((item) => Number(item.quantity) > 0);
      return this.updateCart(firstItem ? { [firstItem.key]: Number(firstItem.quantity) } : {}, {});
    }

    async recoverCartWithSections() {
      const cart = await this.recoverCart();

      try {
        return await this.refreshCartSections(cart);
      } catch (error) {
        console.error('Aerre Rewards section recovery:', error);
        return cart;
      }
    }

    async removeManagedGifts(cart) {
      const inspection = this.inspectCart(cart);

      if (inspection.giftItems.length === 0) {
        return this.refreshCartSections(cart);
      }

      const updates = {};
      inspection.giftItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const updatedCart = await this.updateCart(updates, null);

      if (this.inspectCart(updatedCart).giftItems.length > 0) {
        throw new Error('Aerre Rewards gift lines could not be removed');
      }

      return updatedCart;
    }

    async removeManagedCartLines(cart) {
      const inspection = this.inspectCart(cart);
      const managedItems = [...inspection.membershipItems, ...inspection.giftItems];

      if (managedItems.length === 0) {
        return this.refreshCartSections(cart);
      }

      const updates = {};
      managedItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const updatedCart = await this.updateCart(updates, null);
      const updatedInspection = this.inspectCart(updatedCart);

      if (updatedInspection.membershipItems.length > 0 || updatedInspection.giftItems.length > 0) {
        throw new Error('Aerre Rewards managed cart lines could not be removed');
      }

      return updatedCart;
    }

    async rollbackGiftReplacement(previousGiftItems) {
      let cart = await this.recoverCart();
      let inspection = this.inspectCart(cart);
      const previousKeys = new Set(previousGiftItems.map((item) => item.key));
      const previousVariantIds = previousGiftItems
        .map((item) => Number(item.variant_id))
        .filter((variantId, index, values) => values.indexOf(variantId) === index);
      let keeper = inspection.giftItems.find((item) => previousKeys.has(item.key)) || null;

      if (!keeper && previousVariantIds.length === 1) {
        keeper = inspection.giftItems.find((item) => Number(item.variant_id) === previousVariantIds[0]) || null;
      }

      const updates = {};
      inspection.giftItems.forEach((item) => {
        updates[item.key] = keeper && item.key === keeper.key ? 1 : 0;
      });

      if (inspection.giftItems.length > 0) {
        cart = await this.updateCart(updates, null);
      } else {
        cart = await this.refreshCartSections(cart);
      }

      inspection = this.inspectCart(cart);

      if (inspection.giftItems.length > 1 || (inspection.giftItems.length === 1 && Number(inspection.giftItems[0].quantity) !== 1)) {
        throw new Error('Aerre Rewards gift replacement rollback failed');
      }

      return cart;
    }

    giftSelectionMessage(variantId) {
      const variant = this.giftVariant(variantId);
      const template = String(
        variant?.family === 'discovery'
          ? this.config.strings.giftDiscoverySelected || this.config.strings.giftSelected || ''
          : this.config.strings.giftSelected || ''
      );

      return template
        .replace('__AERRE_GIFT__', variant ? variant.title : '')
        .replace('__AERRE_GIFT_COLOUR__', variant ? variant.title : '')
        .replace(/\{\{\s*colour\s*\}\}/g, variant ? variant.title : '')
        .replace(/\{\{\s*gift\s*\}\}/g, variant ? variant.title : '');
    }

    desiredAvailableGiftVariant(cart) {
      const preferredId = this.preferredGiftVariantId(cart);
      const requested = this.giftVariant(preferredId);
      const inspection = this.inspectCart(cart);
      const requestedAlreadyInCart = requested && inspection.giftItems.some((item) => Number(item.variant_id) === requested.id);

      if (requested && (requested.available || requestedAlreadyInCart)) {
        this.desiredGiftFamily = requested.family;
        this.desiredGiftVariantId = requested.id;
        return requested.id;
      }

      return null;
    }

    initialCombinedGiftVariant(cart, inspection) {
      const hasOrdinaryGiftCandidate = this.normalizeCart(cart).items.some((item) => this.isOrdinaryGiftCandidate(item));

      if (
        !this.gift.enabled
        || !this.gift.valid
        || inspection.membershipItems.length > 0
        || inspection.giftItems.length > 0
        || hasOrdinaryGiftCandidate
      ) {
        return null;
      }

      return this.desiredAvailableGiftVariant(cart);
    }

    async reconcileGift(cart, target, reconcileAttempt = 0) {
      let inspection = this.inspectCart(cart);
      const shouldHaveGift = Boolean(target && inspection.coherent && this.gift.enabled && this.gift.valid);

      if (!shouldHaveGift) {
        if (inspection.giftItems.length > 0) {
          const updates = {};
          inspection.giftItems.forEach((item) => {
            updates[item.key] = 0;
          });
          cart = await this.updateCart(updates, null);
        }

        this.giftLastError = '';
        this.giftAnnouncement = '';
        return cart;
      }

      if (this.hasCreditedOrdinaryGift(cart)) {
        if (inspection.giftItems.length > 0) {
          cart = await this.removeManagedGifts(cart);
          inspection = this.inspectCart(cart);
        }

        if (this.hasCreditedOrdinaryGift(cart)) {
          this.giftLastError = '';
          this.giftAnnouncement = '';
          return cart;
        }

        if (reconcileAttempt >= 1) {
          throw new Error('Aerre Rewards credited gift allocation changed during reconciliation');
        }

        return this.reconcileGift(cart, target, reconcileAttempt + 1);
      }

      const desiredVariantId = this.desiredAvailableGiftVariant(cart);

      if (!desiredVariantId) {
        this.giftLastError = this.config.strings.giftUnavailable;
        this.giftAnnouncement = '';

        if (inspection.giftItems.length > 0) {
          const updates = {};
          inspection.giftItems.forEach((item) => {
            updates[item.key] = 0;
          });
          cart = await this.updateCart(updates, null);
        }

        return cart;
      }

      this.desiredGiftVariantId = desiredVariantId;
      let keeper = inspection.giftItems.find((item) => Number(item.variant_id) === desiredVariantId && this.isCurrentGiftItem(item)) || null;
      const previousGiftItems = inspection.giftItems.map((item) => ({
        key: item.key,
        variant_id: Number(item.variant_id)
      }));
      let replacementAttempted = false;

      try {
        if (!keeper) {
          // Add the replacement first. If any later step fails, roll back the
          // newly attempted colour and keep at most one previously confirmed gift.
          replacementAttempted = true;
          await this.addGift(desiredVariantId);
          cart = await this.recoverCart();
          inspection = this.inspectCart(cart);

          if (this.hasCreditedOrdinaryGift(cart)) {
            cart = await this.removeManagedGifts(cart);

            if (this.hasCreditedOrdinaryGift(cart)) {
              this.giftLastError = '';
              this.giftAnnouncement = '';
              return cart;
            }

            if (reconcileAttempt >= 1) {
              throw new Error('Aerre Rewards credited gift allocation changed during reconciliation');
            }

            return this.reconcileGift(cart, target, reconcileAttempt + 1);
          }

          keeper = inspection.giftItems.find((item) => Number(item.variant_id) === desiredVariantId && this.isCurrentGiftItem(item)) || null;

          if (!keeper) {
            throw new Error('Aerre Rewards gift line was not confirmed');
          }
        }

        const updates = {};
        inspection.giftItems.forEach((item) => {
          updates[item.key] = item.key === keeper.key ? 1 : 0;
        });

        const needsUpdate = inspection.giftItems.some((item) => Number(updates[item.key]) !== Number(item.quantity));
        const needsSectionRefresh = !cart.sections;

        if (needsUpdate || needsSectionRefresh) {
          cart = await this.updateCart(needsUpdate ? updates : { [keeper.key]: 1 }, null);
        }

        inspection = this.inspectCart(cart);

        if (inspection.giftItems.length !== 1 || !this.isCurrentGiftItem(inspection.giftItems[0]) || Number(inspection.giftItems[0].variant_id) !== desiredVariantId || Number(inspection.giftItems[0].quantity) !== 1) {
          throw new Error('Aerre Rewards gift line could not be reconciled');
        }
      } catch (error) {
        if (replacementAttempted) {
          try {
            await this.rollbackGiftReplacement(previousGiftItems);
          } catch (rollbackError) {
            console.error('Aerre Rewards gift rollback:', rollbackError);
          }
        }

        throw error;
      }

      this.giftLastError = '';
      this.giftAnnouncement = this.giftSelectionMessage(desiredVariantId);
      return cart;
    }

    async applyTarget(requestedTarget) {
      let cart = this.cart;
      let inspection = this.inspectCart(cart);
      const target = inspection.hasMerchandise ? requestedTarget : false;

      if (target) {
        const selectedGift = this.giftVariant(this.preferredGiftVariantId(cart));

        if (selectedGift) {
          this.desiredGiftFamily = selectedGift.family;
          this.desiredGiftVariantId = selectedGift.id;

          if (selectedGift.family === 'atomiser') {
            this.desiredAtomiserVariantId = selectedGift.id;
          }
        }
      }

      const attributes = this.targetAttributes(target, inspection.hasMerchandise);
      const updates = {};
      const keeper = target ? inspection.validItems[0] : null;

      inspection.membershipItems.forEach((item) => {
        updates[item.key] = keeper && item.key === keeper.key ? 1 : 0;
      });

      if (!target) {
        inspection.giftItems.forEach((item) => {
          updates[item.key] = 0;
        });
      }

      const needsLineUpdate = Object.keys(updates).some((key) => {
        const item = [...inspection.membershipItems, ...inspection.giftItems].find((candidate) => candidate.key === key);
        return Number(updates[key]) !== Number(item.quantity);
      });
      const needsAdd = target && !keeper;
      const needsAttributeUpdate = !this.attributesMatch(cart, attributes);
      const combinedGiftVariantId = needsAdd ? this.initialCombinedGiftVariant(cart, inspection) : null;

      if (needsLineUpdate || (!needsAdd && needsAttributeUpdate)) {
        cart = await this.updateCart(needsLineUpdate ? updates : {}, attributes);
        inspection = this.inspectCart(cart);
      }

      if (needsAdd) {
        let added = null;
        let membershipOnlyFallbackAttempted = false;

        try {
          added = await this.addMembership(combinedGiftVariantId);
        } catch (error) {
          if (!combinedGiftVariantId) {
            throw error;
          }

          // A mobile connection can lose the response after Shopify has already
          // accepted both lines. Recover before retrying so membership is never
          // duplicated and a partial attempt cannot leave an orphan gift.
          try {
            cart = await this.recoverCart();
          } catch (recoveryError) {
            throw error;
          }

          inspection = this.inspectCart(cart);
          if (!inspection.coherent) {
            if (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0) {
              cart = await this.removeManagedCartLines(cart);
            }

            // If the combined request was rejected because the chosen gift just
            // became unavailable, keep the established fail-open behaviour:
            // confirm membership alone, then let normal gift reconciliation
            // retry or surface the real gift error without blocking checkout.
            membershipOnlyFallbackAttempted = true;
            added = await this.addMembership();
          }
        }

        if (added) {
          const addedItems = Array.isArray(added.items) ? added.items : [added];
          this.cart = this.normalizeCart({ ...cart, items: [...cart.items, ...addedItems] });
        } else {
          this.cart = cart;
        }

        cart = await this.updateCart({}, attributes);
        inspection = this.inspectCart(cart);

        if (combinedGiftVariantId && !inspection.coherent) {
          if (inspection.validItems.length > 0) {
            const repairedMembershipUpdates = {};
            const repairedMembershipKeeper = inspection.validItems[0];
            inspection.membershipItems.forEach((item) => {
              repairedMembershipUpdates[item.key] = item.key === repairedMembershipKeeper.key ? 1 : 0;
            });
            cart = await this.updateCart(repairedMembershipUpdates, attributes);
          } else {
            if (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0) {
              cart = await this.removeManagedCartLines(cart);
            }

            if (!membershipOnlyFallbackAttempted) {
              membershipOnlyFallbackAttempted = true;
              await this.addMembership();
              cart = await this.updateCart({}, attributes);
            }
          }

          inspection = this.inspectCart(cart);
        }
      }

      if (target && !inspection.coherent) {
        throw new Error('Aerre Rewards membership line was not confirmed');
      }

      if (!target && (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0)) {
        throw new Error('Aerre Rewards membership or gift line could not be removed');
      }

      try {
        cart = await this.reconcileGift(cart, target);
      } catch (error) {
        this.giftLastError = this.config.strings.giftError;
        this.giftAnnouncement = '';

        try {
          cart = await this.recoverCartWithSections();
        } catch (recoveryError) {
          console.error('Aerre Rewards gift recovery:', recoveryError);
        }

        // A gift failure must not trap the shopper in the cart. Membership
        // state remains authoritative and ordinary checkout stays available.
        console.error('Aerre Rewards gift:', error);
      }

      return cart;
    }

    enqueue(target) {
      this.desired = Boolean(target);
      this.requested = this.desired;
      this.updateUi();

      if (!this.processing) {
        this.processing = this.processQueue();
      }

      return this.processing;
    }

    async processQueue() {
      while (this.requested !== null) {
        const target = this.requested;
        this.requested = null;
        this.pending = true;
        this.lastError = '';
        this.giftAnnouncement = '';
        this.updateUi();

        try {
          const cart = await this.applyTarget(target);
          this.cart = cart;
          this.confirmed = this.inspectCart(cart).coherent;
          this.lastError = '';
          this.publish(cart);
        } catch (error) {
          this.lastError = this.config.strings.error;

          try {
            this.cart = await this.recoverCartWithSections();
            this.publish(this.cart);
          } catch (recoveryError) {
            console.error('Aerre Rewards recovery:', recoveryError);
          }

          this.confirmed = this.inspectCart(this.cart).coherent;

          if (this.requested === null) {
            this.desired = this.confirmed;
          }

          console.error('Aerre Rewards:', error);
        } finally {
          this.pending = false;
          this.updateUi();
        }
      }

      this.processing = null;
    }

    replaceSections(cart) {
      if (!cart.sections) {
        return;
      }

      Object.values(cart.sections).forEach((sectionHtml) => {
        if (!sectionHtml) {
          return;
        }

        const template = document.createElement('template');
        template.innerHTML = sectionHtml.trim();
        const replacement = template.content.firstElementChild;
        const current = replacement && replacement.id ? document.getElementById(replacement.id) : null;

        // Focal keeps the cart-drawer custom element mounted and refreshes its
        // contents in response to cart:refresh. Replacing the element here
        // would detach that lifecycle before the event is dispatched.
        if (replacement && (replacement.id === 'mini-cart' || replacement.querySelector('#mini-cart'))) {
          return;
        }

        if (replacement && current) {
          current.replaceWith(replacement);
          return;
        }

        const replacementCart = template.content.querySelector('[data-aerre-rewards-cart-section]');
        const currentCart = document.querySelector('[data-aerre-rewards-cart-section]');

        if (replacementCart && currentCart) {
          currentCart.replaceWith(replacementCart);
        }
      });
    }

    publish(cart) {
      this.replaceSections(cart);
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, aerreRewards: true }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, aerreRewards: true }
      }));
      window.setTimeout(() => this.updateUi(), 0);
      window.setTimeout(() => this.updateUi(), 800);
    }

    clearGiftFocusIntent() {
      this.lastSurface = null;
      this.giftFocusElement = null;
      this.giftFocusSelector = null;
    }

    restoreGiftSelectorFocus() {
      const selectionFailed = Boolean(this.lastError || this.giftLastError);

      if (!this.lastSurface || (!this.giftAnnouncement && !selectionFailed) || this.pending || this.checkoutInProgress) {
        return;
      }

      const selector = this.giftFocusSelector || '[data-aerre-rewards-gift-family-input]';
      const control = document.querySelector(`.aerre-rewards-gift--${this.lastSurface} ${selector}`);

      // Wait for Focal to replace the initiating cart surface. A later
      // cart:refresh/section callback will retry against the new selector.
      if (!control || control === this.giftFocusElement || control.disabled) {
        return;
      }

      try {
        control.focus({ preventScroll: true });
      } catch (error) {
        control.focus();
      }

      this.clearGiftFocusIntent();
    }

    updateUi() {
      this.syncStandardProgress();
      const showTreatment = this.adapter.countryEligible && this.adapter.cohort === 'treatment';
      const showAcquisition = showTreatment && !this.config.entitled;
      const visibleCohort = showAcquisition ? 'treatment' : 'control';
      const inspection = this.inspectCart(this.cart);
      const canOffer = this.config.valid && (this.config.available || inspection.membershipItems.length > 0);
      const displayedState = this.pending ? this.desired : this.confirmed;
      const confirmedGiftVariantId = this.confirmedGiftVariantId(this.cart);
      const displayedGiftVariantId = this.pending ? this.desiredGiftVariantId : confirmedGiftVariantId || this.desiredGiftVariantId;
      const displayedGiftVariant = this.giftVariant(displayedGiftVariantId);
      const displayedGiftFamily = displayedGiftVariant?.family || this.desiredGiftFamily;
      const hasConfirmedGift = Boolean(confirmedGiftVariantId);
      const showSafetyGift = !this.operational && Boolean(this.lastError) && inspection.coherent && hasConfirmedGift;
      const showGift = inspection.coherent && hasConfirmedGift && this.gift.enabled && this.gift.valid
        && ((showAcquisition && displayedState) || showSafetyGift);
      let state = displayedState ? 'on' : 'off';
      let status = displayedState ? this.config.strings.on : this.config.strings.off;
      let giftStatus = this.giftAnnouncement;

      if (this.config.entitled) {
        state = 'active';
        status = this.config.strings.active;
      } else if (!canOffer) {
        state = 'unavailable';
        status = this.config.strings.unavailable;
      } else if (this.lastError) {
        state = 'error';
        status = this.lastError;
      } else if (this.giftLastError && displayedState) {
        state = 'error';
        status = this.giftLastError;
      } else if (this.pending) {
        state = 'updating';
        status = this.config.strings.updating;
      }

      if (this.giftLastError) {
        giftStatus = this.giftLastError;
      } else if (this.pending && showGift) {
        giftStatus = this.config.strings.giftUpdating;
      }

      document.querySelectorAll('[data-aerre-rewards-preference]').forEach((component) => {
        component.hidden = !showAcquisition;
        component.dataset.state = state;
        component.setAttribute('aria-busy', this.pending ? 'true' : 'false');
        const toggle = component.querySelector('[data-aerre-rewards-toggle]');
        const statusElement = component.querySelector('[data-aerre-rewards-status]');

        const optionInputs = typeof component.querySelectorAll === 'function'
          ? component.querySelectorAll('[data-aerre-rewards-option-input]')
          : [];

        optionInputs.forEach((input) => {
          const selectsMembership = input.value === 'member';
          input.checked = selectsMembership === displayedState;
          input.disabled = this.checkoutInProgress || this.pending || (selectsMembership && !canOffer);
        });

        if (toggle) {
          toggle.checked = displayedState;
          toggle.disabled = !canOffer || this.checkoutInProgress;
        }

        if (statusElement) {
          statusElement.textContent = status;
        }
      });

      document.querySelectorAll('[data-aerre-rewards-checkout-without]').forEach((button) => {
        button.hidden = !showAcquisition || !displayedState;
        button.disabled = this.checkoutInProgress;
      });

      document.querySelectorAll('[data-aerre-rewards-checkout]').forEach((button) => {
        button.disabled = this.checkoutInProgress;
        button.setAttribute('aria-busy', this.checkoutInProgress ? 'true' : 'false');
      });

      document.querySelectorAll('[data-aerre-rewards-cohort-content]').forEach((element) => {
        element.hidden = element.dataset.aerreRewardsCohortContent !== visibleCohort;
      });

      document.querySelectorAll('[data-aerre-rewards-gift]').forEach((component) => {
        component.hidden = !showGift;
        component.dataset.state = this.giftLastError ? 'error' : this.pending ? 'updating' : 'ready';
        component.setAttribute('aria-busy', this.pending ? 'true' : 'false');
        const colourSelect = component.querySelector('[data-aerre-rewards-gift-colour-select]');
        const discoverySelection = component.querySelector('[data-aerre-rewards-gift-discovery-selection]');
        const statusElement = component.querySelector('[data-aerre-rewards-gift-status]');

        component.querySelectorAll('[data-aerre-rewards-gift-family-input]').forEach((input) => {
          const family = input.value === 'discovery' ? 'discovery' : 'atomiser';
          const hasAvailableFamily = this.gift.variants.some((variant) => variant.family === family && variant.available);
          input.checked = family === displayedGiftFamily;
          input.disabled = this.pending || this.checkoutInProgress || !hasAvailableFamily;
        });

        component.querySelectorAll('[data-aerre-rewards-gift-family-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.aerreRewardsGiftFamilyPanel !== displayedGiftFamily;
        });

        if (colourSelect) {
          const selectedAtomiserId = displayedGiftVariant?.family === 'atomiser'
            ? displayedGiftVariant.id
            : this.desiredAtomiserVariantId;

          if (selectedAtomiserId && this.giftVariant(selectedAtomiserId)) {
            colourSelect.value = String(selectedAtomiserId);
          }

          Array.from(colourSelect.options).forEach((option) => {
            const variant = this.giftVariant(option.value);
            const isConfirmed = Number(option.value) === Number(confirmedGiftVariantId);
            option.disabled = !variant || variant.family !== 'atomiser' || (!variant.available && !isConfirmed);
          });
          colourSelect.disabled = this.pending || this.checkoutInProgress || displayedGiftFamily !== 'atomiser';
        }

        if (discoverySelection) {
          const discovery = displayedGiftVariant?.family === 'discovery'
            ? displayedGiftVariant
            : this.giftVariant(this.rankedDiscoveries(this.cart)[0]?.id);
          discoverySelection.textContent = discovery ? this.giftSelectionMessage(discovery.id) : this.config.strings.giftUnavailable;
        }

        if (statusElement) {
          statusElement.textContent = giftStatus;
        }
      });

      document.querySelectorAll('[data-aerre-rewards-recommendations]').forEach((recommendations) => {
        recommendations.dataset.aerreRewardsSuppressed = String(showGift);
      });

      this.restoreGiftSelectorFocus();
    }

    syncStandardProgress() {
      const source = document.querySelector('[data-aerre-rewards-progress-source]');

      if (!source) {
        return;
      }

      document.querySelectorAll('[data-aerre-rewards-progress-announcement]').forEach((announcement) => {
        if (announcement.innerHTML !== source.innerHTML) {
          announcement.innerHTML = source.innerHTML;
        }
      });
    }

    onChange(event) {
      const giftFamilyInput = event.target.closest('[data-aerre-rewards-gift-family-input]');
      const giftColourSelect = event.target.closest('[data-aerre-rewards-gift-colour-select]');
      const optionInput = event.target.closest('[data-aerre-rewards-option-input]');
      const toggle = event.target.closest('[data-aerre-rewards-toggle]');

      if (giftFamilyInput) {
        const family = giftFamilyInput.value === 'discovery' ? 'discovery' : 'atomiser';
        const inspection = this.inspectCart(this.cart);
        const hasAvailableFamily = this.gift.variants.some((variant) => variant.family === family && variant.available);

        if (!hasAvailableFamily || !inspection.coherent || !this.operational) {
          this.updateUi();
          return;
        }

        this.lastSurface = giftFamilyInput.closest('[data-aerre-rewards-gift]')?.classList.contains('aerre-rewards-gift--page') ? 'page' : 'drawer';
        this.giftFocusElement = giftFamilyInput;
        this.giftFocusSelector = `[data-aerre-rewards-gift-family-input][value="${family}"]`;
        this.desiredGiftFamily = family;
        this.desiredGiftVariantId = this.preferredGiftVariantId(this.cart);
        this.lastError = '';
        this.giftLastError = '';
        this.giftAnnouncement = '';
        this.enqueue(true);
        return;
      }

      if (giftColourSelect) {
        const variant = this.giftVariant(giftColourSelect.value);
        const inspection = this.inspectCart(this.cart);

        if (!variant || variant.family !== 'atomiser' || !variant.available || !inspection.coherent || !this.operational) {
          this.updateUi();
          return;
        }

        this.lastSurface = giftColourSelect.closest('[data-aerre-rewards-gift]')?.classList.contains('aerre-rewards-gift--page') ? 'page' : 'drawer';
        this.giftFocusElement = giftColourSelect;
        this.giftFocusSelector = '[data-aerre-rewards-gift-colour-select]';
        this.desiredGiftFamily = 'atomiser';
        this.desiredAtomiserVariantId = variant.id;
        this.desiredGiftVariantId = variant.id;
        this.lastError = '';
        this.giftLastError = '';
        this.giftAnnouncement = '';
        this.enqueue(true);
        return;
      }

      if (optionInput) {
        if (!this.operational) {
          this.updateUi();
          return;
        }

        this.clearGiftFocusIntent();
        this.lastSurface = optionInput.closest('[data-aerre-rewards-preference]')?.dataset.aerreRewardsSurface || null;
        this.lastError = '';
        this.enqueue(optionInput.value === 'member');
        return;
      }

      if (!toggle || !this.operational) {
        return;
      }

      this.clearGiftFocusIntent();
      this.lastError = '';
      this.enqueue(toggle.checked);
    }

    onDrawerTermsClick(event) {
      const termsTrigger = event.target.closest?.('[data-aerre-rewards-terms-trigger]');
      const drawer = termsTrigger?.closest('#mini-cart');

      if (!drawer) {
        return;
      }

      const announcementTrigger = document.querySelector(
        '.announcement-bar [data-action="open-content"]:not([hidden])'
      );

      if (!announcementTrigger) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (this.termsPopupPending) {
        return;
      }

      this.termsPopupPending = true;
      let finished = false;
      let fallbackId;

      const finish = () => {
        if (finished) {
          return;
        }

        finished = true;
        this.termsPopupPending = false;
        drawer.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(fallbackId);
        announcementTrigger.click();
      };

      const onTransitionEnd = (transitionEvent) => {
        if (transitionEvent.target === drawer && transitionEvent.propertyName === 'transform') {
          finish();
        }
      };

      if (!drawer.open) {
        finish();
        return;
      }

      drawer.addEventListener('transitionend', onTransitionEnd);
      drawer.open = false;
      fallbackId = window.setTimeout(finish, 700);
    }

    async onClick(event) {
      const withoutMembership = event.target.closest('[data-aerre-rewards-checkout-without]');
      const checkout = event.target.closest('[data-aerre-rewards-checkout]');
      const staleManagedState = this.hasManagedStaleState(this.cart);
      const shouldHandleCheckout = this.operational || staleManagedState;

      if (withoutMembership && shouldHandleCheckout) {
        event.preventDefault();
        await this.checkout(false, true);
        return;
      }

      if (checkout && shouldHandleCheckout) {
        const target = this.operational ? this.desired : false;
        event.preventDefault();
        await this.checkout(target, !target);
      }
    }

    onSectionLoad() {
      this.updateUi();
    }

    async checkout(target, strictRemoval) {
      if (this.checkoutInProgress) {
        return;
      }

      this.checkoutInProgress = true;
      this.updateUi();

      try {
        await this.enqueue(target);
        const inspection = this.inspectCart(this.cart);

        if (strictRemoval && (inspection.membershipItems.length > 0 || inspection.giftItems.length > 0)) {
          throw new Error('Aerre Rewards membership or gift remains in the cart');
        }

        if (!this.membershipStateSafeForCheckout(this.cart)) {
          this.lastError = this.config.strings.error;
          this.cart = await this.removeManagedCartLines(this.cart);
          this.confirmed = this.inspectCart(this.cart).coherent;
          this.publish(this.cart);
        }

        if (!this.membershipStateSafeForCheckout(this.cart)) {
          throw new Error('Aerre Rewards membership state remains unsafe for checkout');
        }

        if (!this.giftStateSafeForCheckout(this.cart, target)) {
          this.giftLastError = this.config.strings.giftError;
          this.giftAnnouncement = '';
          this.cart = await this.removeManagedGifts(this.cart);
          this.confirmed = this.inspectCart(this.cart).coherent;
          this.publish(this.cart);
        }

        if (!this.giftStateSafeForCheckout(this.cart, target)) {
          throw new Error('Aerre Rewards gift state remains unsafe for checkout');
        }

        window.location.assign(this.config.checkoutUrl);
      } catch (error) {
        this.lastError = this.config.strings.error;
        console.error('Aerre Rewards checkout:', error);
      } finally {
        this.checkoutInProgress = false;
        this.updateUi();
      }
    }

    onCartUpdated(event) {
      if (!event.detail || !event.detail.cart || event.detail.aerreRewards) {
        return;
      }

      const previous = this.inspectCart(this.cart);
      const incoming = this.normalizeCart(event.detail.cart);
      const next = this.inspectCart(incoming);
      this.cart = incoming;
      this.confirmed = next.coherent;

      if (!this.pending) {
        this.desiredGiftFamily = this.preferredGiftFamily(incoming);
        this.desiredAtomiserVariantId = this.preferredAtomiserVariantId(incoming);
        this.desiredGiftVariantId = this.preferredGiftVariantId(incoming);
      }

      const treatmentEligible = this.adapter.countryEligible && this.adapter.cohort === 'treatment' && !this.config.entitled;

      if (!treatmentEligible) {
        if (this.hasExperimentState(incoming)) {
          this.enqueue(false);
        } else {
          this.updateUi();
        }
        return;
      }

      if (!this.operational) {
        if (this.hasManagedStaleState(incoming)) {
          this.enqueue(false);
        } else {
          this.updateUi();
        }
        return;
      }

      const explicitLineRemoval = previous.coherent && !next.coherent && next.hasMerchandise;
      const target = explicitLineRemoval ? false : this.desiredFromCart(incoming);
      const preference = this.attributeValue(incoming, this.config.preferenceAttribute);
      const shouldReconcile = target
        || explicitLineRemoval
        || next.membershipItems.length > 0
        || next.giftItems.length > 0
        || preference === `on:${this.config.offerVersion}`
        || preference === `off:${this.config.offerVersion}`;

      if (shouldReconcile) {
        this.enqueue(target);
      } else {
        this.updateUi();
      }
    }
  }

  class AerreRewardsMount extends HTMLElement {
    connectedCallback() {
      window.requestAnimationFrame(() => namespace.controller?.updateUi());
    }
  }

  namespace.controller = new AerreRewardsController(namespace.config);

  if (!window.customElements.get('aerre-rewards-mount')) {
    window.customElements.define('aerre-rewards-mount', AerreRewardsMount);
  }

  namespace.controller.start();
})();
